import { useState } from "react";
import axios from "axios";
import { Phone, Mail, MapPin, ArrowUpRight, CheckCircle2 } from "lucide-react";
import Reveal from "@/components/Reveal";
import { SITE } from "@/lib/site";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const FIELDS = [
  { key: "product", label: "What product", placeholder: "e.g. small kitchen appliances", required: true },
  { key: "quantity", label: "Quantity", placeholder: "e.g. 10 pallets / 500 units", required: true },
  { key: "destination", label: "Delivery destination", placeholder: "e.g. Miami, FL or Kingston, Jamaica", required: true },
];

const Contact = () => {
  const [form, setForm] = useState({ name: "", email: "", phone: "", product: "", quantity: "", destination: "", message: "" });
  const [sending, setSending] = useState(false);
  const [error, setError] = useState("");
  const [reference, setReference] = useState("");

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setSending(true);
    try {
      const { data } = await axios.post(`${API}/inquiries`, form, { timeout: 30000 });
      setReference(data.reference);
    } catch (err) {
      const d = err.response?.data?.detail;
      setError(typeof d === "string" ? d : "Could not send your inquiry. Please call us at 954-702-3558.");
    } finally {
      setSending(false);
    }
  };

  const reset = () => {
    setForm({ name: "", email: "", phone: "", product: "", quantity: "", destination: "", message: "" });
    setReference("");
  };

  return (
    <section id="contact" className="pb-24 md:pb-32" data-testid="contact-section">
      <div className="max-w-[1240px] mx-auto px-5 md:px-8">
        <div className="rounded-3xl bg-ink text-white px-6 py-14 md:px-14 md:py-20 grid lg:grid-cols-2 gap-14">
          <div>
            <Reveal>
              <p className="font-mono text-[11px] tracking-[0.3em] uppercase text-white/50" data-testid="contact-eyebrow">
                Contact
              </p>
              <h2
                className="mt-5 font-display font-extrabold tracking-[-0.02em] leading-[1.02] text-[clamp(2rem,4vw,3.2rem)]"
                data-testid="contact-heading"
              >
                Tell us what you need.
              </h2>
              <p className="mt-6 text-[16px] leading-relaxed text-white/70 max-w-md" data-testid="contact-subline">
                Product, quantity, destination — that is all it takes to get a quote.
                Prefer to talk it through? We answer the phone.
              </p>
            </Reveal>

            <Reveal delay={0.1}>
              <div className="mt-9 flex flex-col sm:flex-row gap-4">
                <a
                  href={SITE.phoneHref}
                  data-testid="contact-call-button"
                  className="group inline-flex items-center justify-center gap-2.5 rounded-full bg-accent hover:bg-white text-white hover:text-ink font-semibold px-7 py-4 text-[15px] transition-colors duration-200"
                >
                  <Phone size={17} strokeWidth={2.2} />
                  Call {SITE.phoneDisplay}
                </a>
                <a
                  href={SITE.emailHref}
                  data-testid="contact-email-button"
                  className="inline-flex items-center justify-center gap-2.5 rounded-full border border-white/25 hover:border-white text-white font-semibold px-7 py-4 text-[15px] transition-colors duration-200"
                >
                  <Mail size={17} />
                  Email us
                  <ArrowUpRight size={15} className="opacity-60" />
                </a>
              </div>

              <ul className="mt-10 space-y-3 text-[14px] text-white/60" data-testid="contact-details">
                <li className="flex items-start gap-3">
                  <MapPin size={15} className="text-accent mt-0.5 shrink-0" />
                  {SITE.addressFull}
                </li>
                <li className="flex items-start gap-3">
                  <Phone size={15} className="text-accent mt-0.5 shrink-0" />
                  {SITE.phoneDisplay} — sourcing and coordination by phone and email, no retail storefront
                </li>
              </ul>
            </Reveal>
          </div>

          <Reveal delay={0.12}>
            {reference ? (
              <div
                data-testid="inquiry-success-banner"
                className="h-full min-h-[420px] rounded-2xl bg-white text-ink p-8 md:p-10 flex flex-col items-start justify-center"
                role="status"
              >
                <CheckCircle2 size={40} className="text-accent" />
                <h3 className="mt-6 font-display font-bold text-2xl">Inquiry received.</h3>
                <p className="mt-3 text-[15px] text-slate-600 leading-relaxed">
                  Your reference is{" "}
                  <span className="font-mono font-semibold text-ink">{reference}</span>. We
                  will get back to you by phone or email with availability and pricing.
                </p>
                <p className="mt-3 text-[15px] text-slate-600">
                  Need it faster? Call {SITE.phoneDisplay}.
                </p>
                <button
                  type="button"
                  onClick={reset}
                  data-testid="inquiry-send-another-button"
                  className="mt-8 inline-flex items-center gap-2 rounded-full border border-slate-300 hover:border-ink px-6 py-3 text-[14px] font-semibold text-ink transition-colors duration-200"
                >
                  Send another inquiry
                </button>
              </div>
            ) : (
              <form
                onSubmit={submit}
                data-testid="inquiry-form"
                className="rounded-2xl bg-white text-ink p-7 md:p-9"
                noValidate
              >
                <p className="font-mono text-[10px] tracking-[0.3em] uppercase text-slate-400">
                  Inquiry form
                </p>
                <div className="mt-6 grid sm:grid-cols-2 gap-5">
                  <label className="block sm:col-span-1">
                    <span className="font-mono text-[10px] tracking-[0.2em] uppercase text-slate-500">
                      Name *
                    </span>
                    <input
                      type="text"
                      required
                      value={form.name}
                      onChange={set("name")}
                      data-testid="inquiry-name-input"
                      className="mt-2 w-full rounded-md border border-slate-200 bg-white px-4 py-3 text-[15px] text-ink placeholder:text-slate-300 focus:outline-none focus:ring-2 focus:ring-accent/40 focus:border-accent transition-colors"
                      placeholder="Your name"
                    />
                  </label>
                  <label className="block sm:col-span-1">
                    <span className="font-mono text-[10px] tracking-[0.2em] uppercase text-slate-500">
                      Email *
                    </span>
                    <input
                      type="email"
                      required
                      value={form.email}
                      onChange={set("email")}
                      data-testid="inquiry-email-input"
                      className="mt-2 w-full rounded-md border border-slate-200 bg-white px-4 py-3 text-[15px] text-ink placeholder:text-slate-300 focus:outline-none focus:ring-2 focus:ring-accent/40 focus:border-accent transition-colors"
                      placeholder="you@company.com"
                    />
                  </label>
                  <label className="block sm:col-span-2">
                    <span className="font-mono text-[10px] tracking-[0.2em] uppercase text-slate-500">
                      Phone (optional)
                    </span>
                    <input
                      type="tel"
                      value={form.phone}
                      onChange={set("phone")}
                      data-testid="inquiry-phone-input"
                      className="mt-2 w-full rounded-md border border-slate-200 bg-white px-4 py-3 text-[15px] text-ink placeholder:text-slate-300 focus:outline-none focus:ring-2 focus:ring-accent/40 focus:border-accent transition-colors"
                      placeholder="Best number to reach you"
                    />
                  </label>
                  {FIELDS.map((f) => (
                    <label key={f.key} className="block sm:col-span-1">
                      <span className="font-mono text-[10px] tracking-[0.2em] uppercase text-slate-500">
                        {f.label} *
                      </span>
                      <input
                        type="text"
                        required
                        value={form[f.key]}
                        onChange={set(f.key)}
                        data-testid={`inquiry-${f.key}-input`}
                        className="mt-2 w-full rounded-md border border-slate-200 bg-white px-4 py-3 text-[15px] text-ink placeholder:text-slate-300 focus:outline-none focus:ring-2 focus:ring-accent/40 focus:border-accent transition-colors"
                        placeholder={f.placeholder}
                      />
                    </label>
                  ))}
                  <label className="block sm:col-span-2">
                    <span className="font-mono text-[10px] tracking-[0.2em] uppercase text-slate-500">
                      Anything else? (optional)
                    </span>
                    <textarea
                      rows={3}
                      value={form.message}
                      onChange={set("message")}
                      data-testid="inquiry-message-input"
                      className="mt-2 w-full rounded-md border border-slate-200 bg-white px-4 py-3 text-[15px] text-ink placeholder:text-slate-300 focus:outline-none focus:ring-2 focus:ring-accent/40 focus:border-accent transition-colors resize-none"
                      placeholder="Packaging preferences, timeline, packing details…"
                    />
                  </label>
                </div>

                {error && (
                  <p
                    role="alert"
                    data-testid="inquiry-error-banner"
                    className="mt-5 rounded-md border border-red-200 bg-red-50 text-red-700 text-[14px] px-4 py-3"
                  >
                    {error}
                  </p>
                )}

                <button
                  type="submit"
                  disabled={sending}
                  data-testid="inquiry-submit-button"
                  className="mt-7 w-full inline-flex items-center justify-center gap-2 rounded-full bg-accent hover:bg-accentDeep disabled:opacity-60 text-white font-semibold px-7 py-4 text-[15px] transition-colors duration-200"
                >
                  {sending ? "Sending…" : "Send inquiry"}
                  <ArrowUpRight size={16} />
                </button>
                <p className="mt-4 text-[12px] leading-relaxed text-slate-400">
                  We use your details only to respond to this inquiry — see our Privacy
                  Policy. Wholesale only; call for availability and pricing.
                </p>
              </form>
            )}
          </Reveal>
        </div>
      </div>
    </section>
  );
};

export default Contact;
